/**
 * Vista principal de Entrevistas del Hiring Manager
 * Dashboard para jefes de departamento para gestionar entrevistas con candidatos
 */
import { Badge } from '@/shared/components/Badge'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  CirclePlus,
  RefreshCw,
  AlertCircle
} from 'lucide-react'
import { useHiringManagerInterviews } from '@/hooks/useHiringManagerInterviews'
import { HiringManagerInterviewsList } from '@/features/rh/components/HiringManagerInterviews/HiringManagerInterviewsList'
import { HiringManagerInterviewDetail } from '@/features/rh/components/HiringManagerInterviews/HiringManagerInterviewDetail'
import { ScheduleHiringManagerInterview } from '@/features/rh/components/HiringManagerInterviews/ScheduleHiringManagerInterview'
import { HiringManagerInterviewDecisionModal } from '@/features/rh/components/HiringManagerInterviews/HiringManagerInterviewDecisionModal'
import { ApprovedCandidatesList } from '@/features/rh/components/HiringManagerInterviews/ApprovedCandidatesList'
import { hiringManagerInterviewService } from '@/features/rh/api/hiringManagerInterviewService'
import { apiService } from '@/services/api'
import type { HiringManagerInterview, ApprovedCandidateByArea } from '@/types/hiringManagerInterview'
import { userHasPermission } from '@/shared/lib/roleUtils'
import { useAuth } from '@/shared/hooks/useAuth'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { avisoDeErrorAlCargar } from '@/features/rh/lib/candidatosParaAgendar'

type TabType = 'approved' | 'interviews'

export const HiringManagerInterviewsView: React.FC = () => {
  const { user } = useAuth();
  const canRequesterOwnCandidates = userHasPermission(
    "rh.solicitudes_personal.base.create",
    user?.rbac?.effectivePermissions || [],
  )
  const {
    interviews,
    isLoading,
    error,
    loadInterviews,
    recordDecision,
    deleteInterview,
    uploadResultsFile,
    getPendingDecisions
  } = useHiringManagerInterviews()

  // Estados para pestañas
  const [activeTab, setActiveTab] = useState<TabType>()
  
  // Estados para candidatos aprobados
  const [approvedCandidates, setApprovedCandidates] = useState<ApprovedCandidateByArea[]>([])
  const [isLoadingApproved, setIsLoadingApproved] = useState(false)
  const [approvedError, setApprovedError] = useState<string | null>(null)

  // Estados para modales
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDecisionModal, setShowDecisionModal] = useState(false)
  const [selectedInterview, setSelectedInterview] = useState<HiringManagerInterview | null>(null)
  const [preSelectedCandidateId, setPreSelectedCandidateId] = useState<string | undefined>(undefined)

  // Una notificación puede traer `?entrevista=<id>`: se abre esa entrevista en
  // cuanto llega la lista, una sola vez, y el parámetro se quita para que
  // recargar o volver no la reabra (SRH-324).
  const [params, setParams] = useSearchParams()
  const entrevistaPedida = params.get('entrevista')
  const yaAbierta = useRef(false)
  useEffect(() => {
    if (!entrevistaPedida || yaAbierta.current || isLoading) return
    const buscada = interviews.find((i) => String(i.id) === entrevistaPedida)
    if (!buscada) return
    yaAbierta.current = true
    setSelectedInterview(buscada)
    setShowDetailModal(true)
    const resto = new URLSearchParams(params)
    resto.delete('entrevista')
    setParams(resto, { replace: true })
  }, [entrevistaPedida, interviews, isLoading, params, setParams])

  // Cargar datos al montar
  useEffect(() => {
    if (activeTab === 'approved') {
      loadApprovedCandidates()
    } else {
      loadInterviews()
    }
  }, [loadInterviews, activeTab])

  // Función para cargar candidatos aprobados
  const loadApprovedCandidates = async () => {
    setIsLoadingApproved(true)
    setApprovedError(null)
    try {
      const candidates = await hiringManagerInterviewService.getApprovedCandidatesByArea()
      setApprovedCandidates(candidates)
    } catch (errCrudo: unknown) {
      /**
       * El mensaje del servidor no sirve para enseñarlo: un 403 llega como
       * «Insufficient permissions. Required ONE of: [...]», en inglés y con
       * claves de permisos dentro. Se traduce a qué puede hacer la persona.
       */
      setApprovedError(avisoDeErrorAlCargar(errCrudo).texto)
    } finally {
      setIsLoadingApproved(false)
    }
  }

  // Métricas
  const pendingInterviews = getPendingDecisions()

  // Handlers
  const handleViewDetail = (interview: HiringManagerInterview) => {
    setSelectedInterview(interview)
    setShowDetailModal(true)
  }

  const handleRecordDecision = (interview: HiringManagerInterview) => {
    setSelectedInterview(interview)
    setShowDecisionModal(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteInterview(id)
      // Recargar lista
      await loadInterviews()
    } catch (error) {
      console.error('Error deleting interview:', error)
    }
  }

  const handleScheduleSuccess = async () => {
    await loadInterviews()
  }

  const handleDecisionSubmit = async (decision: any, file?: File) => {
    if (!selectedInterview) return
    
    try {
      // Primero registrar la decisión
      await recordDecision(selectedInterview.id, decision)
      
      // Si hay un archivo, subirlo
      if (file) {
        await uploadResultsFile(selectedInterview.id, file)
      }
      
      // Recargar lista
      await loadInterviews()
      setShowDecisionModal(false)
      setSelectedInterview(null)
    } catch (error) {
      console.error('Error submitting decision:', error)
      throw error // Re-throw para que el modal maneje el error
    }
  }

  const handleRefresh = async () => {
    if (activeTab === 'approved') {
      await loadApprovedCandidates()
    } else {
      await loadInterviews()
    }
  }

  const handleScheduleFromApproved = (candidateId: string) => {
    // Pre-seleccionar el candidato en el modal
    setPreSelectedCandidateId(candidateId)
    setShowScheduleModal(true)
  }

  const handleRejectCandidate = async (candidateId: string, candidateName: string) => {
    try {
      await apiService.updateCandidateStatus(candidateId, 'rechazado')
      // Recargar la lista para reflejar el cambio
      await loadApprovedCandidates()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setApprovedError(err.message || `Error al rechazar al candidato "${candidateName}"`)
      throw err
    }
  }

  useEffect(() => {
    // Establecer la pestaña activa por defecto según permisos
    if (
      userHasPermission(
        "rh.entrevistas.base.read",
        user?.rbac?.effectivePermissions || [],
      )
    ) {
      setActiveTab("interviews");
    } else if (canRequesterOwnCandidates) {
      setActiveTab("approved");
    }
  }, [user, canRequesterOwnCandidates]);

  if (
    userHasPermission(
      "rh.entrevistas.base.read",
      user?.rbac?.effectivePermissions || []
    ) === false &&
    canRequesterOwnCandidates === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative mt-2">
      <div className="w-full max-w-none relative">
        {/* Header */}
        <div className="mb-8 relative z-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <TituloDePantalla>
                Entrevistas del solicitante
              </TituloDePantalla>
              <p className="text-gray-600 mt-1">
                Revisa y administra las entrevistas para tus solicitudes de personal.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className={estiloDeBoton("secundario")}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'girando' : ''}`} />
                Actualizar
              </button>
              {(userHasPermission(
                "rh.entrevistas.base.create",
                user?.rbac?.effectivePermissions || [],
              ) || canRequesterOwnCandidates
              ) && (
                <button
                  onClick={() => {
                    setPreSelectedCandidateId(undefined); // No preseleccionar cuando se abre desde el botón
                    setShowScheduleModal(true);
                  }}
                  className={estiloDeBoton("primario")}
                >
                  <CirclePlus className="w-4 h-4" />
                  Programar Entrevista
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pestañas con el patrón de configuración: subrayado en la activa.
            Antes eran botones azules, distintos a los del resto del sistema. */}
        <nav className="mb-6 flex gap-1 border-b" style={{ borderColor: "#E0E0E0" }} aria-label="Tabs">
          {(userHasPermission(
            "rh.reclutamiento_candidatos.base.read",
            user?.rbac?.effectivePermissions || [],
          ) || canRequesterOwnCandidates) && (
            <button
              onClick={() => setActiveTab("approved")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                activeTab === "approved"
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent hover:border-gray-200"
              }`}
              style={{ color: "#1E1E1F" }}
            >
              Candidatos Aprobados
              {approvedCandidates.length > 0 && (
                <Badge color="morado">{approvedCandidates.length}</Badge>
              )}
            </button>
          )}
          {(userHasPermission(
            "rh.entrevistas.base.read",
            user?.rbac?.effectivePermissions || [],
          ) || canRequesterOwnCandidates) && (
            <button
              onClick={() => setActiveTab("interviews")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                activeTab === "interviews"
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent hover:border-gray-200"
              }`}
              style={{ color: "#1E1E1F" }}
            >
              Entrevistas Programadas
              {interviews.length > 0 && (
                <Badge color="morado">{interviews.length}</Badge>
              )}
            </button>
          )}
        </nav>

        {/* Contenido según pestaña activa */}
        {activeTab === 'approved' ? (
          /* Vista de candidatos aprobados */
          approvedError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-900 mb-1">No se pueden mostrar los candidatos</h3>
                <p className="text-sm text-red-700">{approvedError}</p>
              </div>
            </div>
          ) : (
            <ApprovedCandidatesList
              candidates={approvedCandidates}
              isLoading={isLoadingApproved}
              onScheduleInterview={handleScheduleFromApproved}
              onRejectCandidate={handleRejectCandidate}
            />
          )
        ) : (
          /* Vista original de entrevistas */
          <>
        {/* Alertas */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-900">Error al cargar entrevistas</h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {pendingInterviews.length > 0 && (
          // El alto tope va solo de sm para arriba: ahi el texto entra en dos
          // renglones y el recuadro queda compacto. En pantalla angosta el
          // titulo se parte en tres y recortarlo esconderia el dato.
          <div className="mb-6 px-4 py-3 sm:max-h-[70px] bg-[#FEF7E8] border border-[#F6DFA6] rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#D99F00] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-[16px] font-medium text-[#A97C00]">
                Tienes {pendingInterviews.length} entrevista{pendingInterviews.length !== 1 ? 's' : ''} pendiente{pendingInterviews.length !== 1 ? 's' : ''} de decisión
              </h4>
              <p className="text-[14px] text-[#A97C00]">
                Recuerda registrar tu decisión después de realizar cada entrevista
              </p>
            </div>
          </div>
        )}

        {/* Lista de entrevistas programadas */}
        <HiringManagerInterviewsList
          interviews={interviews}
          isLoading={isLoading}
          onViewDetail={handleViewDetail}
          onRecordDecision={handleRecordDecision}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
        />
          </>
        )}

        {/* Modales */}
        <ScheduleHiringManagerInterview
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false)
            setPreSelectedCandidateId(undefined) // Limpiar preselección al cerrar
          }}
          onSuccess={handleScheduleSuccess}
          preSelectedCandidateId={preSelectedCandidateId}
        />

        <HiringManagerInterviewDetail
          interview={selectedInterview!}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedInterview(null)
          }}
          onRecordDecision={handleRecordDecision}
        />

        <HiringManagerInterviewDecisionModal
          interview={selectedInterview}
          isOpen={showDecisionModal}
          onClose={() => {
            setShowDecisionModal(false)
            setSelectedInterview(null)
          }}
          onSubmit={handleDecisionSubmit}
        />
      </div>
    </div>
  )
}
